/**
 * Modal for saving table view preferences
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
} from '@mui/material';

interface SaveViewModalProps {
  open: boolean;
  onSave: (viewName: string, isPublic: boolean) => Promise<void> | void;
  onClose: () => void;
  isAdmin?: boolean;
}

const SaveViewModal: React.FC<SaveViewModalProps> = ({
  open,
  onSave,
  onClose,
  isAdmin = false,
}) => {
  const [viewName, setViewName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSave = useCallback(async () => {
    if (!viewName.trim()) {
      setError('View name is required');
      return;
    }

    if (viewName.trim().length > 50) {
      setError('View name must be 50 characters or less');
      return;
    }

    setSaving(true);
    try {
      await onSave(viewName.trim(), isPublic);
      setViewName('');
      setIsPublic(false);
      setError('');
    } finally {
      setSaving(false);
    }
  }, [viewName, isPublic, onSave]);

  const handleClose = useCallback(() => {
    if (saving) return;
    setViewName('');
    setIsPublic(false);
    setError('');
    onClose();
  }, [onClose, saving]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth sx={{ zIndex: (theme) => theme.zIndex.modal + 1000 }}>
      <DialogTitle>Save Table View</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="View Name"
            placeholder="e.g., Active Patients, Alphabetical"
            value={viewName}
            onChange={(e) => {
              setViewName(e.target.value);
              setError('');
            }}
            onKeyPress={handleKeyPress}
            error={!!error}
            helperText={error}
            margin="normal"
            disabled={saving}
          />

          {isAdmin && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  disabled={saving}
                />
              }
              label="Make Public (visible to all users)"
              sx={{ mt: 2 }}
            />
          )}

          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            This view will save your current column selection, order, and widths.
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save View'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveViewModal;
